import { useCallback, useEffect, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { naytaPvm } from "./pvm";
import { api, ApiError, type Laippaliitos } from "./api";
import { t, virheTeksti, type KaannosAvain } from "./kieli";
import Laippakiristys, { varoitusTeksti } from "./Laippakiristys";
import { Tilasana, type Tilalaji } from "./Tila";

/** T677 E: kiristyksen tila sanana; hyväksytty = kunnossa, palautettu = vika, kirjattu = odottaa hyväksyntää. */
const KIRISTYS_TILALAJI: Record<string, Tilalaji> = { hyvaksytty: "kunnossa", palautettu: "vika", kirjattu: "varoitus", tuotu: "ei_mitattu" };
function kiristysArvo(l: Laippaliitos): string | null {
  if (l.viimeisin_momentti_nm != null) return `${l.viimeisin_momentti_nm} Nm`;
  if (l.viimeisin_paine_bar != null) return `${l.viimeisin_paine_bar} bar${l.viimeisin_pulttivoima_kn != null ? ` · ${l.viimeisin_pulttivoima_kn} kN` : ""}`;
  return null;
}

/* ═══════════════════════════════════════════════════════════
   Laippaliitokset (133, Toni 18.9.2026).

   Sama lista kelpaa linjalle ja laitteelle, koska liitos voi
   kuulua kumpaan tahansa tai molempiin — siksi komponentti ottaa
   molemmat tunnisteet ja välittää vain sen, joka on annettu.

   264 (T636, Toni 25.10.2026): kiristys on oma tapahtumansa (Laippakiristys.tsx), ja liitoksen rivi avaa sen kiristykset
   ja puhelinlomakkeen. Vanha kuittaus (133: kuittaaja ja momentti liitoksen rivillä) näytetään yhä, jos rivillä on
   sellainen — uutta ei kirjata sillä tavalla.
   ═══════════════════════════════════════════════════════════ */

export default function Laippaliitokset({
  linjaId, laiteId, saaKiristaa, saaKasitella, toisenPuolesta = false, omaHenkilo = null,
}: {
  linjaId?: number;
  laiteId?: number;
  /** Tunnuksen työlaji on laippatyö (263) tai tunnus kirjaa toisen puolesta; palvelin tarkistaa kiristäjän pätevyyden. */
  saaKiristaa: boolean;
  /** Hyväksyjän rooli (pääkäyttäjä, työnjohtaja, hitsauksen työnjohtaja, tarkastaja). */
  saaKasitella: boolean;
  /** Lomakkeessa kenttä «Kiristäjä» (työnjohto, Toni 25.10.2026 klo 20.00). */
  toisenPuolesta?: boolean;
  omaHenkilo?: number | null;
}) {
  const [lista, setLista] = useState<Laippaliitos[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [auki, setAuki] = useState<number | null>(null);

  const uusi = useViimeisin();   // päätös 99
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try {
      const l = await api.laippaliitokset({ linja_id: linjaId, laite_id: laiteId });
      if (!tuore()) return;
      setLista(l);
      setVirhe(null);
    } catch (e) {
      if (!tuore()) return;
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.laippa.hakuvirhe"));
      setLista([]);
    }
  }, [linjaId, laiteId, uusi]);

  useEffect(() => { void lataa(); }, [lataa]);

  if (lista === null) return <p className="dim">{t("muu.laippa.ladataan")}</p>;

  return (
    <div style={{ marginTop: 12, borderTop: "1px solid var(--viiva)", paddingTop: 12 }}>
      <h3 style={{ margin: "0 0 8px" }}>
        {t("muu.laippa.otsikko")}{" "}
        <span className="dim" style={{ fontWeight: 400 }}>
          {lista.length === 0 ? "" : `· ${t("muu.laippa.avoinna", { n: lista.filter((l) => !l.kuitattu && !l.kiristyksia).length, m: lista.length })}`}
        </span>
      </h3>

      {virhe && <p className="huom" role="status">{virhe}</p>}

      {lista.length === 0 ? (
        <p className="dim">{linjaId ? t("muu.laippa.ei_linjalla") : t("muu.laippa.ei_laitteella")}</p>
      ) : (
        /* Toni 26.10.2026 klo 04.15: puhelimella (375 px) taulukko levensi linjasivun 606 px:iin — vierii nyt omassa säiliössään. */
        <div className="taulu-ikkuna" data-taulu-ikkuna="laippaliitokset">
        <table className="taulu">
          <thead>
            <tr>
              <th>{t("iv.tunnus")}</th><th>{t("muu.laippa.koko")}</th><th>{t("muu.laippa.paineluokka")}</th><th>{t("muu.laippa.tiiviste")}</th>
              <th>{t("muu.laippa.vaadittu")}</th><th>{t("muu.laippa.viimeisin_kiristys")}</th><th>{t("lk.kiristykset")}</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((l) => (
              <tr key={l.id}>
                <td className="mono">{l.tunnus}</td>
                <td>{l.koko_dn ?? <span className="dim">–</span>}</td>
                <td>{l.paineluokka ?? <span className="dim">–</span>}</td>
                <td>{l.tiiviste ?? <span className="dim">–</span>}</td>
                <td className="mono">{l.vaadittu_nm ?? <span className="dim">{t("muu.laippa.ei_vaatimusta")}</span>}</td>
                <td data-lk-viimeisin={l.id}>
                  {/* T677 E (Toni 27.10.2026 klo 09.08): viimeisimmän kiristyksen arvo, tila ja ⚠ suoraan rivillä — ennen sarake luki 133:n
                      kuittauskenttää, jota kiristysloki ei kirjoita, ja näytti «–» vaikka 95 Nm oli kirjattu. Vanha kuittaus näkyy yhä. */}
                  {l.viimeisin_tila ? (
                    <>
                      <span className="mono">{kiristysArvo(l) ?? <span className="dim">{t("muu.laippa.ei_lukemaa")}</span>}</span>
                      {" "}<Tilasana laji={KIRISTYS_TILALAJI[l.viimeisin_tila] ?? "ei_mitattu"}>{t(`lk.tila.${l.viimeisin_tila}` as KaannosAvain)}</Tilasana>
                      {(l.viimeisin_varoitukset ?? []).length > 0 && (
                        <div className="huom huom--varoitus" data-lk-rivivaroitus={l.id}>⚠ {(l.viimeisin_varoitukset ?? []).map(varoitusTeksti).join(" · ")}</div>
                      )}
                    </>
                  ) : l.toteutunut_nm != null ? <span className="mono">{l.toteutunut_nm} Nm</span>
                    : l.kuitattu ? <span className="dim">{t("muu.laippa.ei_lukemaa")}</span>
                    : <span className="dim">–</span>}
                </td>
                <td>
                  {l.kuitattu && <div>{naytaPvm(l.paiva)} · {l.kuittaaja ?? <span className="dim">–</span>}</div>}
                  <button className="nappi nappi--kevyt" style={{ minHeight: 44 }} data-lk-liitos={l.id}
                          aria-expanded={auki === l.id} onClick={() => setAuki(auki === l.id ? null : l.id)}>
                    {t("lk.kiristykset")} ({l.kiristyksia ?? 0})
                    {l.viimeisin_tila ? ` · ${t(`lk.tila.${l.viimeisin_tila}` as KaannosAvain)} ${naytaPvm(l.viimeisin_paiva)}` : ""}
                  </button>
                </td>
              </tr>
            )).flatMap((rivi, i) => auki === lista[i]!.id ? [rivi, (
              <tr key={`k-${lista[i]!.id}`}><td colSpan={7}>
                <Laippakiristys liitosId={lista[i]!.id} saaKiristaa={saaKiristaa} saaKasitella={saaKasitella}
                               toisenPuolesta={toisenPuolesta} omaHenkilo={omaHenkilo} onMuutos={() => void lataa()} />
              </td></tr>
            )] : [rivi])}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
